import useAftermarketPurchaseFromQuery from "@modules/purchase/warranty/hooks/useAftermarketPurchaseFromQuery";
import { useRouter } from "next/router";
import useSourceInfoFromQuery from "@modules/query-string/hooks/useSourceInfoFromQuery";
import { useTranslation } from "next-i18next";
import {
  SlideToggle,
  Button,
  Listbox,
  Form,
  Combobox,
} from "@talladega/components";
import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { Controller, SubmitHandler, useFormContext } from "react-hook-form";
import { ArrowRightIcon } from "@talladega/icons";
import {
  vehicleValidation,
  translate,
  onSubmitError,
} from "@talladega/helpers";
import useDebounce from "@common/hooks/useDebounce";
import { useVehicleLookupByNameByUniqueTrim } from "@modules/vehicle/hooks/useVehicleLookup";
import { useNullableNumberMask } from "@common/hooks/useMask";
import { provinceFilters } from "@modules/purchase/warranty/hooks/useEligibleProvinces";
import useProvincesStatusBySourceList from "@modules/purchase/warranty/hooks/useProvincesStatusBySourceList";
import useUser from "@modules/user/hooks/useUser";
import useVehicleDisplayFuelTypes from "@modules/vehicle/hooks/useVehicleDisplayFuelTypes";
import { VinInput } from "@common/components/form/vehicle";
import useAftermarketPurchase from "@modules/purchase/warranty/hooks/useAftermarketPurchase";
import { tryGetActiveAftermarketPurchase } from "@modules/purchase/warranty/types/Purchase";
import WhereIsMyVinRoadwiseImage from "@public/images/where-is-my-vin-roadwise.png";
import { TranslationKeys } from "src/@types/i18next";
import { WarrantySegmentationFormProps } from "../types/WarrantySegmentationFormProps";
import {
  WarrantySegmentationMode,
  quotePersonalFormMode,
} from "../types/WarrantySegmentationMode";
import NoCoveragesModal from "./coverage/NoCoveragesModal";

const warrantyQuoteFormFocusOrder = [
  "yearMakeModel",
  "vin",
  "fuelType",
  "odometer",
  "province",
  "promoCode",
];

type Props = {
  onChangeMode(newMode: WarrantySegmentationMode): void;
  setVehicleSearchMode(mode: "ymm" | "vin"): void;
  vehicleSearchMode: "ymm" | "vin";
};

const WarrantyQuoteVehicleInfoForm = ({
  onChangeMode,
  setVehicleSearchMode,
  vehicleSearchMode,
}: Props) => {
  const { t } = useTranslation(["common", "purchase"]);
  const router = useRouter();
  const { locale: rlocale } = router;
  const locale = rlocale || "en";
  const { user } = useUser();

  const aftermarketPurchaseFromQuery = useAftermarketPurchaseFromQuery();
  const { source: querySource } = useSourceInfoFromQuery();

  const { purchase: purchaseResponse, isLoading: isPurchaseLoading } =
    useAftermarketPurchase();
  const purchase = tryGetActiveAftermarketPurchase(purchaseResponse);

  const { vin: buyerVin } = aftermarketPurchaseFromQuery;

  const provincesList = useProvincesStatusBySourceList(
    querySource,
    provinceFilters.isAftermarketEnabled
  );

  const { fuelTypes } = useVehicleDisplayFuelTypes();
  const fuelTypeOptions: { id: number; name: string }[] | null = useMemo(
    () =>
      fuelTypes.length > 0
        ? fuelTypes.map(({ id, name }) => ({
            id,
            name: translate(name, locale),
          }))
        : null,
    [fuelTypes, locale]
  );

  const { maskPipe: numberMaskPipe } = useNullableNumberMask();

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
    getValues,
  } = useFormContext<WarrantySegmentationFormProps>();

  const [firstLoad, setFirstLoad] = useState(true);

  useEffect(() => {
    if (router.isReady && firstLoad && buyerVin) {
      if (buyerVin) {
        setVehicleSearchMode("vin");
      }
      setFirstLoad(false);
    }
  }, [buyerVin, firstLoad, router.isReady, setVehicleSearchMode]);

  // hack: on locale change, re-run the number mask
  useEffect(() => {
    const v = getValues("odometer");
    if (v != null) {
      setValue("odometer", numberMaskPipe(v.replace(/[^\d.]/, "")));
    }
  }, [getValues, numberMaskPipe, router.locale, setValue]);

  const [showNoCoveragesModal, setShowNoCoveragesModal] = useState(false);

  const handleFormSubmit: SubmitHandler<WarrantySegmentationFormProps> = () => {
    onChangeMode(quotePersonalFormMode);
  };

  const [ymmQuery, setYmmQuery] = useState<string>("");

  const debouncedQuery = useDebounce(ymmQuery, 300);
  const { vehicles: vehiclesFound } = useVehicleLookupByNameByUniqueTrim(
    debouncedQuery,
    `aftermarket-${querySource}${purchase?.id ? `:${purchase.id}` : ""}`.trim(),
    !isPurchaseLoading
  );

  return (
    <>
      <form
        onSubmit={handleSubmit(
          handleFormSubmit,
          onSubmitError(warrantyQuoteFormFocusOrder)
        )}
      >
        <div className="grid grid-cols-1 gap-3">
          <div className="sm:col-span-2">
            <SlideToggle
              leftLabel={t("common:year_make_model")}
              leftValue="ymm"
              rightLabel={t("common:vin")}
              rightValue="vin"
              value={vehicleSearchMode}
              onChange={setVehicleSearchMode}
              rounded="md"
            />
          </div>
          <div className="flex flex-col gap-3">
            {vehicleSearchMode === "ymm" && (
              <>
                <Controller
                  name="yearMakeModel"
                  control={control}
                  rules={{
                    required: {
                      value: true,
                      message: t("common:vehicle_description_required"),
                    },
                  }}
                  render={({ field: { value, onChange, ref } }) => (
                    <Combobox
                      label={t("common:year_make_and_model")}
                      items={vehiclesFound}
                      keyOption={(item) => item.name}
                      displayOption={(item) => item?.name ?? ""}
                      displayButton={(item) => item?.name ?? ""}
                      valueOption={(item) => item}
                      selectedValue={value}
                      onChange={onChange}
                      onInputChange={setYmmQuery}
                      hideDropdownBtn
                      status={errors.yearMakeModel ? "error" : "default"}
                      feedback={errors.yearMakeModel?.message}
                      itemsFilter="none"
                      itemsTextDisplay="full"
                      inputRef={ref}
                    />
                  )}
                />
                {fuelTypeOptions && (
                  <Controller
                    name="fuelType"
                    control={control}
                    rules={{
                      required: {
                        value: true,
                        message: t("common:fuel_type_required"),
                      },
                    }}
                    render={({ field: { onChange, ref } }) => (
                      <Listbox
                        label={t("common:fuel_type")}
                        onChange={onChange}
                        selectedItem={watch("fuelType")}
                        items={fuelTypeOptions}
                        displayOption={(item) => item?.name ?? ""}
                        displayButton={(item) =>
                          item?.name || t("common:select_fuel_type")
                        }
                        keyOption={(p) => p.id}
                        valueOption={(p) => p}
                        buttonRef={ref}
                        status={errors.fuelType ? "error" : "default"}
                        feedback={errors.fuelType?.message}
                      />
                    )}
                  />
                )}
              </>
            )}
            {vehicleSearchMode === "vin" && (
              <VinInput
                name="vin"
                labelOption="short"
                register={register}
                errors={errors}
                required
                showWhereIsMyVin
                whereIsMyVinImg={WhereIsMyVinRoadwiseImage}
              />
            )}
            <Form.Input
              label={t("common:odometer")}
              maxLength={vehicleValidation.odometer.maxLength}
              status={errors.odometer ? "error" : "default"}
              feedback={errors.odometer?.message}
              {...register("odometer", {
                required: {
                  value: true,
                  message: t("common:form_error_required_odometer"),
                },
                maxLength: vehicleValidation.odometer.maxLength,
                min: {
                  value: 1,
                  message: t("purchase:odometer_cannot_be_zero"),
                },
                onChange: (e) => {
                  e.target.value = numberMaskPipe(e.target.value);
                },
              })}
            />
            <Controller
              name="province"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t("common:province_required"),
                },
              }}
              render={({ field: { onChange, ref } }) => (
                <Listbox
                  label={t("common:province")}
                  onChange={onChange}
                  selectedItem={watch("province")}
                  items={provincesList}
                  keyOption={(p) => p.id}
                  displayOption={(item) =>
                    item.isEnabled
                      ? t(item.name as TranslationKeys["common"])
                      : `${t(item.name as TranslationKeys["common"])} - ${t(
                          "common:coming_soon"
                        )}`
                  }
                  displayButton={(item) =>
                    item?.name != null
                      ? t(item.name as TranslationKeys["common"])
                      : t("common:select_province")
                  }
                  disabledOption={(item) => !item.isEnabled}
                  valueOption={(p) => p}
                  buttonRef={ref}
                  status={errors.province ? "error" : "default"}
                  feedback={errors.province?.message}
                />
              )}
            />
            <Form.Input
              label={t("purchase:promo_code")}
              feedback={errors.promoCode?.message}
              status={errors.promoCode ? "error" : "default"}
              {...register("promoCode", {
                maxLength: {
                  value: 20,
                  message: t("common:must_be_fewer_characters", {
                    count: 20,
                  }),
                },
                onChange: (e: ChangeEvent<HTMLInputElement>) => {
                  e.target.value = e.target.value.trim().toUpperCase();
                },
              })}
            />
          </div>
        </div>
        <div className="mt-6 flex flex-col gap-4 w-full">
          <div className="flex flex-col items-stretch space-y-4 w-full">
            <div className="hidden text-center sm:block">
              <Button
                type="submit"
                className="w-full"
                size="extra-large"
                rightIcon={<ArrowRightIcon />}
              >
                {t("purchase:get_a_free_quote")}
              </Button>
            </div>
            <div className="block text-center sm:hidden">
              <Button
                type="submit"
                className="w-full"
                rightIcon={<ArrowRightIcon />}
              >
                {t("purchase:get_a_free_quote")}
              </Button>
            </div>
          </div>
        </div>
      </form>
      {showNoCoveragesModal && (
        <NoCoveragesModal
          showNoCoveragesModal={showNoCoveragesModal}
          setShowNoCoveragesModal={setShowNoCoveragesModal}
          onPrimaryClick={() => setShowNoCoveragesModal(false)}
          primaryButtonText={t("common:okay")}
          modalMode="Generic"
          userId={user?.id}
        />
      )}
    </>
  );
};

export default WarrantyQuoteVehicleInfoForm;
